
  /* eslint-disable @typescript-eslint/camelcase */
  import { Vue, Component } from "vue-property-decorator";
  import AuthMixin from "../../mixins/auth.mixin";
  import { apiHandler } from "@/services/api-handler";
  import { AggregatedOrderResponse } from "shared/api/routes/order";
  import { Api } from "shared/api";
  import { IUser } from "shared/entities/user";
  import { DateTime } from "luxon";
  import * as XLSX from "xlsx";

  declare module "vue/types/vue" {
    interface Vue {
      user: IUser | null;
    }
  }
  @Component
  export default class ViewSales extends Vue.extend(AuthMixin) {
    aggregatedOrders: AggregatedOrderResponse = [];
    selectedYear = 2023;
    selectedWeekNumber = 1;
    userLocations: number[] = [];
    selectedLocation: number | null = null;
    signature="";
    selectedLocationRoyaltyPercent = 0;
    selectedLocationAdvertisingPercent = 0;
    selectedLocationTechFee = 0;
    startDate: Date | null = null;
  endDate: Date | null = null;
  isLoading= false;

  downloadExcel() {
    // Create a new workbook and a worksheet
    const wb = XLSX.utils.book_new();
    const ws_name = "SalesData";
    const ws_data = [
      ["Royalty Sales", "Point of Sale", "", "", "", "", "Other Channels", "", "", "", "", ""],
      ["", "Main", "Main %", "Sides", "Sides %", "Catering", "Catering %", "DoorDash", "DoorDash %", "UberEats", "UberEats %", "Online", "Online %", "Mobile", "Mobile %"],
      [this.formatAmount(this.calculateTotalOrdersAmount()), this.formatAmount(this.calculateTotalForType("Main")), this.calculatePercentage("Main") + "%", this.formatAmount(this.calculateTotalForType("Sides")), this.calculatePercentage("Sides") + "%", this.formatAmount(this.calculateTotalForType("Catering")), this.calculatePercentage("Catering") + "%", this.formatAmount(this.calculateTotalForPos("Doordash")), this.calculatePercentageForPos("Doordash") + "%", this.formatAmount(this.calculateTotalForPos("UberEats")), this.calculatePercentageForPos("UberEats") + "%", this.formatAmount(this.calculateTotalForPos("Online")), this.calculatePercentageForPos("Online") + "%", this.formatAmount(this.calculateTotalForPos("Mobile")), this.calculatePercentageForPos("Mobile") + "%"]
    ];

    // Add data to worksheet
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    XLSX.utils.book_append_sheet(wb, ws, ws_name);

    // Write workbook and trigger download
    XLSX.writeFile(wb, "SalesData.xlsx");
  }

  formatAmount(amount: number | string): string {
    // Check if the amount is already a number or if it's a numeric string
    const isNumeric = (val: string | number): boolean => {
        return !isNaN(parseFloat(val.toString())) && isFinite(val as number);
    };

    if (!isNumeric(amount)) {
        // eslint-disable-next-line no-console
        console.error("formatAmount: amount is not a number", amount);
        return "$0.00";
    }

    // Convert to number if it's a numeric string
    const numericAmount = typeof amount === "string" ? parseFloat(amount) : amount;

    // Handle negative numbers: You might want to adjust this part if negative amounts should be treated differently
    if (numericAmount < 0) {
        // Format the number with commas, two decimal places, and show it's negative
        return `-$${Math.abs(numericAmount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    }

    // Format the number with commas and two decimal places for positive numbers
    return `$${numericAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  }

  ordersTotal(): number {
    const main = Math.max(0, this.calculateTotalForType("Main"));
    const sides = Math.max(0, this.calculateTotalForType("Sides"));
    const catering = Math.max(0, this.calculateTotalForType("Catering"));
    const total = main + sides + catering;
    return total;
  }

  posTotal(): number {
    const doordash = Math.max(0, this.calculateTotalForPos("Doordash"));
    const uber = Math.max(0, this.calculateTotalForPos("UberEats"));
    const online = Math.max(0, this.calculateTotalForPos("Online"));
    const mobile = Math.max(0, this.calculateTotalForPos("Mobile"));
    const total = doordash + uber + online + mobile;
    return total;
  }

  calculateTotalOrdersAmount(): number {
      // Assuming aggregatedOrders is an array of objects, each with a 'data' array of orders
      return this.aggregatedOrders.reduce((totalSum, dailyData) => {
          const dailyTotal = dailyData.data.reduce((dailySum: number, order: { totalAmount: string; }) => {
              const amount = parseFloat(order.totalAmount);
              return dailySum + (isNaN(amount) ? 0 : amount);
          }, 0);
          return totalSum + dailyTotal;
      }, 0);
  }


  calculateTotalForType(itemType: string): number {
    return this.aggregatedOrders.reduce((totalSum, dailyData) => {
        const typeTotal = dailyData.data.reduce((typeSum: number, order: { itemType: string; totalAmount: string; }) => {
            if (order.itemType === itemType) {
                const amount = parseFloat(order.totalAmount);
                return typeSum + (isNaN(amount) ? 0 : amount);
            }
            return typeSum;
        }, 0);
        return totalSum + typeTotal;
    }, 0);
  }


  calculatePercentage(itemType: string): number {
    const totalSales = this.ordersTotal();
    if (totalSales === 0) {
      return 0;
    }
    const totalForType = Math.max(0,this.calculateTotalForType(itemType));
    return parseFloat(((totalForType / totalSales) * 100).toFixed(2));
  }

  calculateTotalForPos(posType: string): number {
  return this.aggregatedOrders.reduce((totalSum, dailyData) => {
      const posTotal = dailyData.data.reduce((posSum: number, order: { pointOfSale: string; totalAmount: string; }) => {
          if (order.pointOfSale === posType) {
              const amount = parseFloat(order.totalAmount);
              return posSum + (isNaN(amount) ? 0 : amount);
          }
          return posSum;
      }, 0);
      return totalSum + posTotal;
    }, 0);
  }

calculatePercentageForPos(posType: string): number {
    const totalSales = this.posTotal();
    if (totalSales === 0) {
        return 0;
    }
    const totalForPos = Math.max(0,this.calculateTotalForPos(posType));
    return parseFloat(((totalForPos / totalSales) * 100).toFixed(2));
}

    async fetchWeekData() {
      if (!this.selectedLocation) {
          // eslint-disable-next-line no-console
          console.error("No location selected");
          return;
      }
      try {
          // Directly receive the processed data from the API call
          const weekData = await apiHandler(() => Api.weeks[this.selectedYear][this.selectedWeekNumber].Get());
          await this.fetchOrders(weekData.week_start, weekData.week_end, this.selectedLocation);
      } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error fetching week data:", error);
      }
      if (this.selectedLocation) {
        await this.fetchLocationDetails(this.selectedLocation);
      }
  }

  async fetchOrders(startDate: Date, endDate: Date, location: number) {
    this.aggregatedOrders = []; // Clear previous orders data

    try {
      const requestBody = {
        startDate,
        endDate,
        location
      };

      // Make a POST request and directly get the response data
      const responseData: AggregatedOrderResponse = await apiHandler(() => Api.orders.Post(requestBody));
      this.aggregatedOrders.push(...responseData);
      // eslint-disable-next-line no-console
      // console.log(this.aggregatedOrders[0]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching orders:", error);
    }
  }

  async fetchLocationById(locationId: number | null) {
    if (locationId === null) {
      throw new Error("Location ID is null");
    }

    // Use apiHandler to get the location data directly
    try {
      const locationData = await apiHandler(() => Api.locations[locationId].Get());
      return locationData; // Assuming the API returns the full location object
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching location by ID:", error);
      throw error; // Re-throw the error to handle it in the calling function
    }
  }


  async fetchWeekByYearIdAndWeekNumberId(yearId: number, weekNumberId: number) {
    try {
      // Use apiHandler to get the week data directly
      const weekData = await apiHandler(() => Api.weeks[yearId][weekNumberId].Get());
      return weekData; // Assuming the API returns the week data
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching week data:", error);
      return null; // Return null or handle the error as needed
    }
  }

    async fetchLocationDetails(locationId: number) {
      const locationData = await this.fetchLocationById(locationId);
      if (locationData) {
        this.selectedLocationRoyaltyPercent = locationData.percent;
        this.selectedLocationAdvertisingPercent = locationData.advertising;
        this.selectedLocationTechFee = locationData.tech;
      }
    }


    async fetchSalesData() {
      this.isLoading = true;
      if (!this.startDate || !this.endDate) {
        // eslint-disable-next-line no-console
        console.error("Date range not selected");
        this.isLoading = false;
        return;
      }
      if (!this.selectedLocation) {
        // eslint-disable-next-line no-console
        console.error("No location selected");
        this.isLoading = false;
        return;
      }

      try {
        // Interpret the selected dates as being in the local time zone (e.g., EST)
        // and adjust them to the start and end of the day
        const start = DateTime.fromISO(this.startDate).setZone("America/New_York").startOf("day");
        const end = DateTime.fromISO(this.endDate).setZone("America/New_York").endOf("day");
        // Convert these adjusted dates to UTC
        const formattedStartDate = start.toUTC().toISO();
        const formattedEndDate = end.toUTC().toISO();

        // Make sure both dates are valid before calling fetchOrders
        if (formattedStartDate && formattedEndDate) {
          await this.fetchOrders(formattedStartDate, formattedEndDate, this.selectedLocation);
        } else {
            // eslint-disable-next-line no-console
          console.error("Invalid date range");
        }

      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching sales data:", error);
      }finally {
        this.isLoading = false; // Ensure loading is stopped in case of success or failure
      }
    }



  async mounted() {
    if (this.user?.id) {
      try {
        const locationIds = await apiHandler(() => Api.user[this.user.id].locations.Get());
        // Fetch full location objects and update userLocations
        this.userLocations = await Promise.all(locationIds.map(async (id: number | null) => {
          const location = await this.fetchLocationById(id);
          return { id, name: location.name }; // Assuming fetchLocationById returns the full location object
        }));
        // Set the default selected location
        if (this.userLocations.length > 0) {
          this.selectedLocation = this.userLocations[0].id;
        }
      } catch (error) {
          // eslint-disable-next-line no-console
        console.error("Error fetching user locations:", error);
      }
    }
    if (this.selectedLocation) {
        await this.fetchLocationDetails(this.selectedLocation);
      }
  }

  }
  